import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesLeft,
  faAnglesRight,
  faClipboardCheck,
  faClock,
  faEye,
  faListCheck,
  faRotate,
  faSearch,
  faSpinner,
  faTicket,
} from "@fortawesome/free-solid-svg-icons";
import Breadcrumb from "../../../components/Ui_Element/Breadcrumb";
import { getCurrentUserIdentity } from "../../Tickets/ticketUtils";
import { fetchEmployees, getMyTaskObserversPaged, getTaskById } from "../taskService";
import TaskHistoryModal from "../TasksModels/TaskHistoryModal";
import {
  formatTaskDate,
  getTaskPriorityClasses,
  getTaskStatusClasses,
  toDateInputValue,
} from "../taskUtils";

const STATUS_FILTERS = ["All", "Pending", "In Progress", "Waiting", "Forwarded", "Completed", "Cancelled"];
const PRIORITY_FILTERS = ["All", "Low", "Medium", "High", "Urgent"];
const SOURCE_FILTERS = [
  { value: "All", label: "All Sources" },
  { value: "Assignee", label: "Assignee" },
  { value: "Creator", label: "Creator" },
  { value: "Manual", label: "Manually Added" },
  { value: "Forward", label: "Forwarded" },
  { value: "ForwardSync", label: "Forward Sync" },
];
const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

const getDefaultStartDate = () => {
  const date = new Date();
  date.setDate(1);
  return toDateInputValue(date);
};

const getDefaultEndDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 1, 0);
  return toDateInputValue(date);
};
const DEFAULT_PAGE_SIZE = 10;
const MAX_VISIBLE_PAGES = 9;

const sourceLabel = (source) => SOURCE_FILTERS.find((s) => s.value === source)?.label || source || "Manual";

export default function MyTaskObserversPage() {
  const location = useLocation();
  const identity = useMemo(() => getCurrentUserIdentity(), []);
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedUserId = searchParams.get("userId") || identity.userId || "";

  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyTask, setHistoryTask] = useState(null);
  const [loadingHistoryTaskId, setLoadingHistoryTaskId] = useState(null);
  const [error, setError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [assigneeFilter, setAssigneeFilter] = useState("All");
  const [startDateFilter, setStartDateFilter] = useState(getDefaultStartDate);
  const [endDateFilter, setEndDateFilter] = useState(getDefaultEndDate);
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [sourceFilter, setSourceFilter] = useState("All");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageMeta, setPageMeta] = useState({
    totalCount: 0,
    totalPages: 0,
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setPageNumber(1);
      setSearchText(searchInput.trim());
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    let active = true;

    const loadEmployees = async () => {
      try {
        const data = await fetchEmployees();
        if (active) setEmployees(data);
      } catch {
        if (active) setEmployees([]);
      }
    };

    loadEmployees();

    return () => {
      active = false;
    };
  }, []);

  const loadItems = React.useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await getMyTaskObserversPaged({
        userId: requestedUserId,
        pageNumber,
        pageSize,
        searchText,
        status: statusFilter,
        assignedEmployeeId: assigneeFilter === "All" ? 0 : Number(assigneeFilter),
        startDate: startDateFilter,
        endDate: endDateFilter,
        priority: priorityFilter,
        source: sourceFilter,
      });

      setItems(data.items || []);
      setPageMeta({
        totalCount: data.totalCount || 0,
        totalPages: data.totalPages || 0,
        pageNumber: data.pageNumber || 1,
        pageSize: data.pageSize || DEFAULT_PAGE_SIZE,
        hasNextPage: Boolean(data.hasNextPage),
        hasPreviousPage: Boolean(data.hasPreviousPage),
      });
    } catch (loadError) {
      setItems([]);
      setPageMeta((current) => ({
        ...current,
        totalCount: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      }));
      setError(loadError?.message || "Observer list could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [requestedUserId, pageNumber, pageSize, searchText, statusFilter, assigneeFilter, startDateFilter, endDateFilter, priorityFilter, sourceFilter]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleViewTimeline = async (taskId) => {
    if (!taskId || loadingHistoryTaskId) return;
    setLoadingHistoryTaskId(taskId);
    setError("");
    try {
      const task = await getTaskById(taskId);
      setHistoryTask(task);
    } catch (timelineError) {
      setError(timelineError?.message || "Task timeline could not be loaded.");
    } finally {
      setLoadingHistoryTaskId(null);
    }
  };

  const paginationNumbers = useMemo(
    () => buildVisiblePageNumbers(pageMeta.pageNumber, pageMeta.totalPages, MAX_VISIBLE_PAGES),
    [pageMeta.pageNumber, pageMeta.totalPages]
  );

  const rangeLabel = useMemo(() => {
    if (!pageMeta.totalCount || !items.length) return "Showing 0 items";

    const start = (pageMeta.pageNumber - 1) * pageMeta.pageSize + 1;
    const end = start + items.length - 1;
    return `Showing ${start}-${end} of ${pageMeta.totalCount}`;
  }, [items.length, pageMeta.pageNumber, pageMeta.pageSize, pageMeta.totalCount]);

  const filterInputClass =
    "rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-slate-400 dark:border-gray-700 dark:bg-gray-800 dark:text-white";

  const actionBtnClass =
    "inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700";

  return (
    <div className="min-h-screen bg-gray-50 p-4 dark:bg-[#0F111A] md:p-6">
      <Breadcrumb pageName="Task Observers" currentPage="Task Observers" />

      <div className="mt-4 overflow-hidden rounded-3xl border border-orange-100 bg-white shadow-xl dark:border-gray-800 dark:bg-[#161B22]" style={{ boxShadow: "0 16px 48px rgba(192,76,42,0.10)" }}>
        <div className="relative overflow-hidden px-5 py-6 text-white md:px-7 md:py-8" style={{ background: "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)" }}>
          <div className="absolute right-0 top-0 h-36 w-36 translate-x-10 -translate-y-8 rounded-full bg-white/10 blur-3xl" />
          <div className="h-[3px] absolute bottom-0 left-0 right-0" style={{ background: "linear-gradient(to right, rgba(255,255,255,0.35), rgba(255,255,255,0.05) 60%, transparent)" }} />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-orange-100 backdrop-blur-sm">
                <FontAwesomeIcon icon={faClipboardCheck} />
                Task Observers
              </div>
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-orange-100">
                <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 backdrop-blur-sm">Logged in as {identity.fullName || "User"}</span>
                <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 backdrop-blur-sm">User ID: {requestedUserId || "Unavailable"}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={loadItems}
                className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-3 text-sm font-bold transition hover:bg-orange-50"
                style={{ color: "#C04C2A" }}
              >
                <FontAwesomeIcon icon={loading ? faSpinner : faRotate} spin={loading} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="p-5 md:p-6">
          <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <input
                type="date"
                value={startDateFilter}
                max={endDateFilter || undefined}
                onChange={(event) => {
                  setStartDateFilter(event.target.value);
                  setPageNumber(1);
                }}
                title="Start date"
                className={filterInputClass}
              />

              <input
                type="date"
                value={endDateFilter}
                min={startDateFilter || undefined}
                onChange={(event) => {
                  setEndDateFilter(event.target.value);
                  setPageNumber(1);
                }}
                title="End date"
                className={filterInputClass}
              />

              <select
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPageNumber(1);
                }}
                className={filterInputClass}
              >
                {STATUS_FILTERS.map((status) => (
                  <option key={status} value={status}>
                    {status === "All" ? "All Status" : status}
                  </option>
                ))}
              </select>

              <select
                value={assigneeFilter}
                onChange={(event) => {
                  setAssigneeFilter(event.target.value);
                  setPageNumber(1);
                }}
                className={filterInputClass}
              >
                <option value="All">All Assignees</option>
                {employees.map((employee) => (
                  <option key={employee.id || employee.userId} value={employee.id}>
                    {employee.fullName} {employee.workEmail ? `- ${employee.workEmail}` : ""}
                  </option>
                ))}
              </select>

              <select
                value={priorityFilter}
                onChange={(event) => {
                  setPriorityFilter(event.target.value);
                  setPageNumber(1);
                }}
                className={filterInputClass}
              >
                {PRIORITY_FILTERS.map((priority) => (
                  <option key={priority} value={priority}>
                    {priority === "All" ? "All Priorities" : priority}
                  </option>
                ))}
              </select>

              <select
                value={sourceFilter}
                onChange={(event) => {
                  setSourceFilter(event.target.value);
                  setPageNumber(1);
                }}
                className={filterInputClass}
              >
                {SOURCE_FILTERS.map((source) => (
                  <option key={source.value} value={source.value}>
                    {source.label}
                  </option>
                ))}
              </select>

              <div className="relative min-w-[220px]">
                <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search task, ticket, or observer"
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-slate-400 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                />
              </div>

              <select
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value) || DEFAULT_PAGE_SIZE);
                  setPageNumber(1);
                }}
                className={filterInputClass}
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    Page Size: {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error ? (
            <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400">{error}</div>
          ) : null}

          {loading ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-6 py-14 text-center text-sm text-slate-500">
              <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
              Loading task observers...
            </div>
          ) : items.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-200 dark:border-gray-800">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-200 bg-white text-sm dark:divide-gray-800 dark:bg-[#161B22]">
                    <thead className="bg-slate-50 dark:bg-gray-800/50">
                      <tr className="text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-gray-400">
                        <th className="px-4 py-3">Task</th>
                        <th className="px-4 py-3">Ticket</th>
                        <th className="px-4 py-3">Assignee</th>
                        <th className="px-4 py-3">Priority</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Due</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                      {items.map((item) => (
                        <tr
                          key={`${item.observerId}-${item.taskId}`}
                          className="align-top hover:bg-slate-50/70 dark:hover:bg-gray-800"
                        >
                          <td className="px-4 py-4">
                            <div className="font-semibold text-slate-900 dark:text-white">{item.taskTitle || "Untitled task"}</div>
                            <div className="mt-1 line-clamp-2 max-w-md text-sm text-slate-500 dark:text-gray-400">{item.taskDescription || "No description"}</div>
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                              <span>
                                Observer: {item.observerEmployeeName || "Unknown"} added on {formatTaskDate(item.createdAt, true)}
                              </span>
                              <span className="rounded-full border border-teal-200 bg-teal-50 px-2.5 py-1 font-semibold text-teal-700 dark:border-teal-900/40 dark:bg-teal-900/20 dark:text-teal-300">
                                {sourceLabel(item.source)}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {item.ticketId ? (
                              <Link
                                to={`/tickets/${item.ticketId}`}
                                state={{ backTo: `${location.pathname}${location.search}`, viewOnly: true }}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                              >
                                <FontAwesomeIcon icon={faTicket} />
                                {item.ticketNumber || "Open Ticket"}
                              </Link>
                            ) : (
                              <span className="text-slate-400">Manual task</span>
                            )}
                            <div className="mt-2 text-sm text-slate-500 dark:text-gray-400">{item.ticketSubject || "No linked ticket subject"}</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="font-medium text-slate-900 dark:text-white">{item.assignedEmployeeName || "Unassigned"}</div>
                            <div className="mt-1 text-xs text-slate-500 dark:text-gray-400">{item.assignedEmployeeEmail || item.assignedUserId || "No email"}</div>
                            <div className="mt-3 flex flex-wrap gap-2 text-xs">
                              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">
                                Observer
                              </span>
                              <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 font-semibold text-slate-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                                {item.observerEmployeeName || "Unknown observer"}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${getTaskPriorityClasses(item.taskPriority)}`}>
                              {item.taskPriority}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="space-y-2">
                              <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getTaskStatusClasses(item.taskStatus)}`}>
                                {item.taskStatus}
                              </span>
                              <div className="text-xs text-slate-500 dark:text-gray-400">Task progress: {item.taskProgressPercent || 0}%</div>
                              <div className="text-xs text-slate-500 dark:text-gray-400">Added by: {item.addedByName || "System"}</div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="font-medium text-slate-900 dark:text-white">{formatTaskDate(item.taskDueDate)}</div>
                            <div className="mt-1 text-xs text-slate-500 dark:text-gray-400">Observer since {formatTaskDate(item.createdAt, true)}</div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex justify-end gap-2">
                              {item.taskId ? (
                                <Link to={`/tasks/${item.taskId}`} className={actionBtnClass}>
                                  <FontAwesomeIcon icon={faEye} className="text-[10px]" />
                                  View
                                </Link>
                              ) : null}
                              {item.taskId ? (
                                <button
                                  type="button"
                                  onClick={() => handleViewTimeline(item.taskId)}
                                  disabled={loadingHistoryTaskId === item.taskId}
                                  className={`${actionBtnClass} disabled:cursor-not-allowed disabled:opacity-60`}
                                >
                                  <FontAwesomeIcon
                                    icon={loadingHistoryTaskId === item.taskId ? faSpinner : faClock}
                                    spin={loadingHistoryTaskId === item.taskId}
                                    className="text-[10px]"
                                  />
                                  Timeline
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-4 border-t border-slate-200 pt-5 dark:border-gray-800 lg:flex-row lg:items-center lg:justify-between">
                <div className="text-sm text-slate-500 dark:text-gray-400">{rangeLabel}</div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPageNumber(1)}
                    disabled={!pageMeta.hasPreviousPage}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    <FontAwesomeIcon icon={faAnglesLeft} />
                  </button>
                  {paginationNumbers.map((page) => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setPageNumber(page)}
                      className={`inline-flex h-10 min-w-10 items-center justify-center rounded-full border px-3 text-sm font-semibold transition ${page === pageMeta.pageNumber
                        ? "border-[#C04C2A] text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:border-orange-200 hover:text-[#C04C2A] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                        }`}
                      style={page === pageMeta.pageNumber ? { background: "linear-gradient(135deg, #C04C2A 0%, #8B2A15 100%)" } : undefined}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPageNumber(pageMeta.totalPages || 1)}
                    disabled={!pageMeta.hasNextPage}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    <FontAwesomeIcon icon={faAnglesRight} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center dark:border-gray-700 dark:bg-gray-800/50">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl text-white" style={{ background: "linear-gradient(135deg, #C04C2A 0%, #8B2A15 55%, #4A1B13 100%)" }}>
                <FontAwesomeIcon icon={faListCheck} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">No observer records found</h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-gray-400">No TaskObserver record matched this user and filter yet.</p>
            </div>
          )}
        </div>
      </div>

      {historyTask ? <TaskHistoryModal task={historyTask} onClose={() => setHistoryTask(null)} /> : null}
    </div>
  );
}

function buildVisiblePageNumbers(currentPage, totalPages, maxVisiblePages) {
  if (!totalPages) return [];

  const safeMax = Math.max(1, maxVisiblePages);
  let startPage = Math.max(1, currentPage - Math.floor(safeMax / 2));
  let endPage = startPage + safeMax - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - safeMax + 1);
  }

  const pages = [];
  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page);
  }

  return pages;
}
